import {
    CheckCircleRounded,
    FileDownloadRounded,
    FileUploadRounded,
    LibraryAddRounded,
    WarningRounded
} from "@mui/icons-material";
import {Button, Checkbox, Chip, Grid, ListItemText, MenuItem, TextField} from "@mui/material";
import {useNavigate} from "react-router-dom";
import {useTranslation} from "react-i18next";
import SectionHeader from "../../../../util/component/SectionHeader";
import {AgGridReact} from "ag-grid-react";
import * as React from "react";
import {useContext, useEffect, useMemo, useRef, useState} from "react";
import {ColDef, GetRowIdParams, GridApi, GridOptions, IServerSideGetRowsParams} from "ag-grid-enterprise";
import {useAgGridLocale, useHasScope} from "../../../../config/globalConfig";
import AdvancedFilter from "../../component/AdvancedFilter";
import * as Yup from 'yup';
import AgGridCustomTable from "../../../../util/component/AgGridCustomTable";
import {
    POI_STATUS_DISMISSED,
    POI_STATUS_DRAFT,
    POI_STATUS_READY,
    POI_TYPE_TEMPLATE_DMS
} from "../../component/Constants";
import {usePoiActions} from "../../service/usePoiActions";
import Stack from "@mui/material/Stack";
import PoiDetailModal from "./modals/PoiDetailModal";
import {useStatusValuesPoi} from "../../component/useStatusValuesPoi";
import {setSnackbar} from "../../../../util/state/globalAction";
import {snackbarTypes} from "../../../../util/state/globalVariables";
import {GlobalContext} from "../../../../util/state/GlobalContextProvider";
import CustomModal from "../../component/CustomModal";
import {PoiService, PoiTemplate} from "./PoiInterfaces";
import EmptyState from "../../../../util/component/EmptyState";
import ActionButtonWithTooltip, {ActionTypes} from "../../component/ActionButtonWithTooltip";
import {downloadTemplate as donwloadPoiTemplate} from "../../component/Utils";
import {keyPrefixValues} from "../../component/ImportFromFileErrorsTable";
import useDownloadTemplate from "../../../salesmarketing/hooks/useDownloadTemplate";
import FileUploader from "../../../../util/component/FileUploader";
import {Controller, useForm} from "react-hook-form";


const PoiListPage = () => {
    const {globalData} = useContext(GlobalContext);
    const {t} = useTranslation(['salesmarketing'], {keyPrefix: 'poi'});
    const {t: tr} = useTranslation("translation");
    const navigate = useNavigate();
    const {getPoiList, importFile, downloadTemplate, getPoiServices, getTemplates} = usePoiActions();
    const {getStatusColorPoi, getStatusLabelPoi} = useStatusValuesPoi();

    const [selectedPoi, setSelectedPoi] = useState<any>(null);
    const [openDetail, setOpenDetail] = useState(false);
    const filterDefaultValues = {
        legalEntity: globalData?.user?.legalEntities?.map((le: any) => le.name),
        dealer: globalData?.user?.dealers?.map((d: any) => d.name),
        status: [], address: "", type: [], service: [], name: "", city: ''
    };
    const [filters, setFilters] = useState<any>(filterDefaultValues);
    const [searchInput, setSearchInput] = useState("");
    const [fileForImport, setFileForImport] = useState<File | undefined>(undefined);
    const [poiTableShouldUpdate, setPoiTableShouldUpdate] = useState(false);
    const [openUploadWarning, setOpenUploadWarning] = useState(false);
    const [openUploadFile, setOpenUploadFile] = useState(false);
    const [poiServices, setPoiServices] = useState<PoiService[]>([]);
    const [templates, setTemplates] = useState<PoiTemplate[]>([])
    const {dispatchGlobalData} = useContext(GlobalContext);
    const hasScope = useHasScope;

    const agGridLocale = useAgGridLocale();
    const gridRef = useRef<AgGridReact>(null);

    const legalEntityOptions: any[] = globalData?.user.legalEntities
        ?.filter((x: any) => x.name.toLowerCase() !== "all")
        .map((x: any) => ({
            value: x.name,
            label: tr(`legalEntities.${x.name}`),
        }));

    const dealerOptions: any[] = globalData?.user.dealers
        ?.filter((x: any) => x.name.toLowerCase() !== "all")
        .map((x: any) => ({
            value: x.name,
            label: tr(`dealers.${x.name}`),
        }));

    const [dealers, setDealers] = React.useState<any[]>(dealerOptions);
    const {watch, control, setValue} = useForm<any>({});
    const {
        importFromFileResponseResultStatus: poiImportFromFileResponseResultStatus,
        renderModalContent: renderPoiModalContent
    } = useDownloadTemplate(
        keyPrefixValues.POI,
        fileForImport,
        setFileForImport,
        importFile,
        (numberOfInsertedPoi: number) => t(`importSuccess.content`, {count: numberOfInsertedPoi}),
        setOpenUploadWarning,
        watch('legalEntity'),
        watch('dealer'),
        setPoiTableShouldUpdate
    )

    const handleChangeLegalEntities = (value: any) => {
        if (value == '') {
            setDealers([]);
            return;
        }
        let dealers = globalData?.user?.dealers
            ?.filter(
                (x: any) =>
                    x.legalEntities.some((e: any) => value == e.name) &&
                    x.name.toLowerCase() !== "all"
            )
            .map((x: any) => {
                return {
                    label: tr(`dealers.${x.name}`),
                    value: x.name,
                };
            });
        setDealers(dealers);
    }

    useEffect(() => {
        getPoiServices().then((res: any) => {
            setPoiServices(res.result);
        });
    }, [])

    useEffect(() => {
        getTemplates().then((res: any) => {
            setTemplates(res.result);
        })
    }, [])

    useEffect(() => {
        setValue('legalEntity', '');
        setValue('dealer', '');
    }, [openUploadWarning]);

    const columns: ColDef[] = [
        {
            field: 'id',
            headerName: t('listColumns.id'),
            unSortIcon: true,
            pinned: 'left',
            lockPosition: 'left',
            lockPinned: true,
            maxWidth: 130
        },
        {
            field: "tenantId",
            headerName: t('legalEntityField'),
            hide: globalData?.user?.legalEntities?.length <= 1,
            unSortIcon: true,
            cellRenderer: (params: any) => {
                return globalData?.user?.legalEntities.find((le: any) => le.name == params.data.tenantId)?.name
            },
            tooltipValueGetter: (params: any) => {
                return globalData?.user?.legalEntities.find((le: any) => le.name == params.data.tenantId)?.name
            }
        },
        {
            field: "dealerId",
            headerName: t('dealerField'),
            hide: globalData?.user?.dealers?.length <= 1,
            unSortIcon: true,
            cellRenderer: (params: any) => {
                return globalData?.user?.dealers.find((le: any) => le.name == params.data.dealerId)?.name
            },
            tooltipValueGetter: (params: any) => {
                return globalData?.user?.dealers.find((le: any) => le.name == params.data.dealerId)?.name
            }
        },
        {
            field: "name",
            headerName: t('listColumns.name'),
            unSortIcon: true,
            tooltipField: "name",
        },
        {
            field: "idPoiServiceList",
            headerName: t('listColumns.service'),
            //unSortIcon: true,
            sortable: false,
            cellRenderer: (params: any) => {
                let formattedList = params.data.idPoiServiceList.map((elem: any) => getStatusLabelPoi(poiServices?.find((ps: PoiService) => ps.id === elem)?.value || '')).sort();
                return formattedList.join(", ");
            },
            tooltipValueGetter: (params: any) => {
                let formattedList = params.data.idPoiServiceList.map((elem: any) => getStatusLabelPoi(poiServices?.find((ps: PoiService) => ps.id === elem)?.value || '')).sort();
                return formattedList.join(", ");
            }
        },
        {
            field: "poiTemplateDetailDTOList",
            headerName: t('listColumns.type'),
            //unSortIcon: true,
            sortable: false,
            cellRenderer: (params: any) => {
                if (params.data.poiTemplateDetailDTOList) {
                    let formattedList = params.data.poiTemplateDetailDTOList.map((elem: any) => getStatusLabelPoi(elem.template));
                    return formattedList.join(", ");
                }
                return '-';
            },
            tooltipValueGetter: (params: any) => {
                if (params.data.poiTemplateDetailDTOList) {
                    let formattedList = params.data.poiTemplateDetailDTOList.map((elem: any) => getStatusLabelPoi(elem.template));
                    return formattedList.join(", ");
                }
                return '-';
            },
        },
        {
            field: "address",
            headerName: t('listColumns.address'),
            tooltipField: "address",
            unSortIcon: true
        },
        {
            field: "city",
            headerName: t('listColumns.city'),
            tooltipField: "city",
            unSortIcon: true
        },
        {
            field: "dmsId",
            headerName: t('listColumns.dmsId'),
            //unSortIcon: true,
            sortable: false,
            cellRenderer: (params: any) => {
                let dms = params.data.poiTemplateDetailDTOList.find((el: any) => el.type === POI_TYPE_TEMPLATE_DMS);
                if (params.data.poiTemplateDetailDTOList && dms && dms.dmsId)
                    return dms.dmsId;
                return '--'
            }
        },
        {
            field: "status",
            maxWidth: 160,
            headerName: t('listColumns.status'),
            pinned: 'right',
            lockPosition: 'right',
            lockPinned: true,
            cellClass: "grid-cell-center",
            headerClass: "grid-header-center",
            unSortIcon: true,
            cellRenderer: (params: any) => {
                return <Chip label={getStatusLabelPoi(params.data.status.toUpperCase())}
                             sx={{backgroundColor: getStatusColorPoi(params.data.status.toUpperCase())}}/>
            }
        },
        {
            field: "_actions",
            maxWidth: 160,
            headerName: t('listColumns.action'),
            pinned: 'right',
            lockPosition: 'right',
            lockPinned: true,
            sortable: false,
            cellRenderer: (params: any) => {
                return (
                    <Stack direction={"row"} justifyContent={"center"}>
                        <ActionButtonWithTooltip type={ActionTypes.edit} callback={() => {
                            window.open(`/salesmarketing/poiupdate/${params.data.id}`, "_blank")
                        }} auth={'SCOPE_SM_UPDATE_POI'}
                        />
                        <ActionButtonWithTooltip type={ActionTypes.details} callback={() => {
                            setOpenDetail(true);
                            setSelectedPoi(params.data);
                        }} auth={'SCOPE_SM_VIEW_POI_DETAIL'}/>
                    </Stack>
                );
            }
        }
    ];

    const statuses = [
        {
            value: POI_STATUS_DRAFT,
            label: getStatusLabelPoi(POI_STATUS_DRAFT),
            color: getStatusColorPoi(POI_STATUS_DRAFT)
        },
        {
            value: POI_STATUS_READY,
            label: getStatusLabelPoi(POI_STATUS_READY),
            color: getStatusColorPoi(POI_STATUS_READY)
        },
        {
            value: POI_STATUS_DISMISSED,
            label: getStatusLabelPoi(POI_STATUS_DISMISSED),
            color: getStatusColorPoi(POI_STATUS_DISMISSED)
        }

    ];

    function createDatasource(filters: any, api: GridApi) {
        return {
            getRows: async (params: IServerSideGetRowsParams) => {
                let filterTemp = {...filters};
                filterTemp.offset = params.request.startRow ? params.request.startRow : 0;
                filterTemp.limit = (params.request.endRow ? params.request.endRow : 10) - filterTemp.offset;
                filterTemp.sort = params.request.sortModel.length === 0 ? null : params.request.sortModel[0].colId;
                filterTemp.order = params.request.sortModel.length === 0 ? null : params.request.sortModel[0].sort;
                filterTemp.search = searchInput;
                if (filterTemp.status.length === 0)
                    filterTemp.status = ['READY', 'DRAFT'];
                try {
                    let data = await getPoiList(filterTemp);

                    params.success({
                        rowData: data?.content,
                        rowCount: data?.totalElements
                    });
                    if (data.totalElements === 0) {
                        api.showNoRowsOverlay();
                    } else {
                        api.hideOverlay();
                    }
                } catch (e) {
                    params.fail();
                }
            }
        };
    }

    const gridOptions: GridOptions = {
        getRowId: (params: GetRowIdParams) => params.data['id'],
        suppressRowClickSelection: true,
        domLayout: 'autoHeight',
        rowModelType: 'serverSide',
        pagination: true,
        paginationPageSize: 10,
        cacheBlockSize: 10,
        defaultColDef: {
            sortable: true,
            resizable: true,
            wrapHeaderText: false,
            minWidth: 130
        },
        columnDefs: columns,
        serverSideInfiniteScroll: true,
        localeText: agGridLocale,
        noRowsOverlayComponentParams: {title: t('noRows')}
    };

    useEffect(() => {
        gridRef?.current?.api?.setServerSideDatasource(createDatasource(filters, gridRef.current!.api));
    }, [filters]);

    useEffect(() => {
        if (poiTableShouldUpdate) {
            gridRef?.current?.api?.setServerSideDatasource(createDatasource(filters, gridRef.current!.api));
            setPoiTableShouldUpdate(false);
        }
    }, [poiTableShouldUpdate]);

    const noRowsOverlayComponent = useMemo<any>(() => {
        return EmptyState;
    }, []);


    const validationObj = {
        status: Yup.array(),
        segment: Yup.string().nullable(),
        startDate: Yup.date().nullable()
            .test("testAgreementStartDate", t('datesCheck'), (value, context) => {
                return (!value || !context.parent.endDate || value <= context.parent.endDate)
            })
            .typeError(t('invalidDate')),
        endDate: Yup.date().nullable()
            .test("testAgreementEndDate", t('datesCheck'), (value, context) => {
                return (!value || !context.parent.startDate || value >= context.parent.startDate)
            })
            .typeError(t('invalidDate')),
        name: Yup.string().nullable(),
        city: Yup.string().nullable(),
        description: Yup.string().nullable(),
    }

    const renderService = (field: any) => {
        return <TextField
            fullWidth
            label={t('filters.service')}
            select
            SelectProps={{
                multiple: true,
                renderValue: (selected: any) => {
                    let result = poiServices.length > 0 ? selected.map((l: any) => getStatusLabelPoi(poiServices.find((ll: any) => ll.id === l)?.value.toUpperCase() || '')).join(', ') : "";
                    return result.length > 100 ? result.substring(0, 100) + "..." : result;
                },
            }}
            value={field.value}
            onChange={field.onChange}
        >
            {poiServices?.map((l: any) => (
                <MenuItem key={l.id} value={l.id}>
                    <Checkbox color={"accent"}
                              checked={field.value.indexOf(l.id) > -1}/>
                    <ListItemText primary={getStatusLabelPoi(l.value.toUpperCase())}/>
                </MenuItem>
            ))}
        </TextField>

    }

    const renderAddress = (field: any) => (
        <TextField
            fullWidth
            label={t('filters.address')}
            value={field.value}
            onChange={field.onChange}
        />
    )

    const renderCity = (field: any) => (
        <TextField
            fullWidth
            label={t('filters.city')}
            value={field.value}
            onChange={field.onChange}
        />
    )

    const renderName = (field: any) => (
        <TextField
            fullWidth
            label={t('filters.name')}
            value={field.value}
            onChange={field.onChange}
        />
    )

    const renderType = (field: any) => (
        <TextField
            fullWidth
            label={t('filters.type')}
            select
            SelectProps={{
                multiple: true,
                renderValue: (selected: any) => selected.map((s: any) => getStatusLabelPoi(templates.find((t: PoiTemplate) => t.type === s)!.type || '')).join(', ')
            }}
            value={field.value}
            onChange={field.onChange}
        >
            {templates?.map((t) => (
                <MenuItem key={t.id} value={t.type}>
                    <Checkbox checked={field.value.indexOf(t.type) > -1}/>
                    <ListItemText primary={getStatusLabelPoi(t.type)}/>
                </MenuItem>
            ))}
        </TextField>
    )

    const renderStatus = (field: any) => (
        <TextField
            fullWidth
            label={t('filters.status')}
            select
            SelectProps={{
                multiple: true,
                renderValue: (selected: any) => selected.map((s: any) => statuses.find(ss => ss.value === s)!.label).join(', ')
            }}
            value={field.value}
            onChange={field.onChange}
        >
            {statuses?.map((status) => (
                <MenuItem key={status.value} value={status.value}>
                    <Checkbox checked={field.value.indexOf(status.value) > -1}/>
                    <span style={{
                        border: "1px solid " + status.color,
                        backgroundColor: status.color,
                        borderRadius: "50%",
                        display: "inline-block",
                        height: "14px",
                        width: "14px",
                        marginRight: "5px"
                    }}/>
                    <ListItemText primary={status.label}/>
                </MenuItem>
            ))}
        </TextField>
    )

    const fields = {
        name: renderName,
        service: renderService,
        address: renderAddress,
        type: renderType,
        status: renderStatus,
        city: renderCity
    }

    const toolbar = {
        renderActions: () =>
            <>
                {hasScope('SCOPE_SM_POI_DOWNLOAD_TEMPLATE') ?
                    <Button sx={
                        {whiteSpace: 'nowrap'}
                    } fullWidth variant={"outlined"} size="small"
                            onClick={() => donwloadPoiTemplate(downloadTemplate, dispatchGlobalData, setSnackbar, snackbarTypes, t("downloadError"))
                            }
                            startIcon={<FileDownloadRounded/>}>
                        {t('buttons.downloadCsvTemplate')}
                    </Button> : <></>
                }
                {hasScope('SCOPE_SM_IMPORT_POI') &&
                    <Button sx={{whiteSpace: 'nowrap'}} fullWidth variant="outlined" size={"small"}
                            onClick={() => {
                                setOpenUploadFile(true);
                            }}
                            component="label" startIcon={<FileUploadRounded/>}>
                        {t('buttons.importFromFile')}
                    </Button>
                }
                {hasScope('SCOPE_SM_CREATE_POI') ?
                    <Button fullWidth size={"small"} startIcon={<LibraryAddRounded/>} onClick={() => {
                        navigate('/salesmarketing/poicreate')
                    }}>
                        {t('buttons.newPoi')}
                    </Button> : <></>
                }
            </>
    }

    return (
        <>
            <SectionHeader title={t('listHeader.title')}
                           subtitle={t('listHeader.subTitle')}/>

            <AdvancedFilter validationObj={validationObj} fields={fields} defaultValues={filterDefaultValues}
                            searchInput={searchInput} setSearchInput={setSearchInput} filters={filters}
                            setFilters={setFilters}/>

            {poiServices.length > 0 &&
                <Grid container>
                    <Grid item xs={12} sx={{padding: "16px"}}>
                        <AgGridCustomTable
                            gridOptions={gridOptions} gridRef={gridRef} toolbar={toolbar}
                            noRowsOverlayComponent={noRowsOverlayComponent}
                            onGridReady={(params) => {
                                params.api.setServerSideDatasource(createDatasource(filters, gridRef.current!.api));
                            }}
                        />
                    </Grid>
                </Grid>
            }
            {selectedPoi &&
                <PoiDetailModal key={selectedPoi.id} open={openDetail}
                                handleClose={() => setOpenDetail(false)}
                                poi={selectedPoi}
                                services={poiServices}
                                templates={templates}
                />
            }
            <CustomModal open={openUploadWarning} onClose={() => setOpenUploadWarning(false)}
                         title={poiImportFromFileResponseResultStatus.isImportFromFileSuccess ? t('importSuccess.title') : t('importErrors.title')}
                         cancelButton={t('buttons.close')}
                         content={renderPoiModalContent}
                         titleIcon={!poiImportFromFileResponseResultStatus.isImportFromFileSuccess ?
                             <WarningRounded color={"error"}/> :
                             <CheckCircleRounded color={"success"}/>}
            />
            <CustomModal open={openUploadFile}
                         onClose={() => {
                             setOpenUploadFile(false);
                             setDealers([]);
                         }}
                         title={t('buttons.importFromFile')}
                         cancelButton={t('buttons.close')}
                         content={() => (
                             <>
                                 {legalEntityOptions.length > 1 &&
                                     <>
                                         <Grid container spacing={2} sx={{padding: "15px"}} alignItems={"center"}>
                                             <Grid item xs={6}>
                                                 <Controller
                                                     name={"legalEntity"}
                                                     control={control}
                                                     render={({field, fieldState: {error}}) => (
                                                         <TextField
                                                             label={tr("shared.legalEntities")}
                                                             fullWidth
                                                             required
                                                             select
                                                             error={!!error}
                                                             helperText={
                                                                 (error && error.type === 'required' && t('forms.required-field', {label: tr("shared.legalEntities")})) ||
                                                                 error?.message
                                                             }
                                                             {...field}
                                                             onChange={(e: any) => {
                                                                 handleChangeLegalEntities(e.target.value);
                                                                 field.onChange(e);
                                                             }}
                                                         >
                                                             {legalEntityOptions.map((option: any) => (
                                                                 <MenuItem key={option.value} value={option.value}>
                                                                     {option.label}
                                                                 </MenuItem>
                                                             ))}
                                                         </TextField>
                                                     )}
                                                 />
                                             </Grid>

                                             <Grid item md={6} sm={3} xs={12}>
                                                 <Controller
                                                     name={"dealer"}
                                                     control={control}
                                                     render={({field, fieldState: {error}}) => (
                                                         <TextField
                                                             label={tr("shared.dealers")}
                                                             fullWidth
                                                             required
                                                             select
                                                             error={!!error}
                                                             helperText={
                                                                 (error && error.type === 'required' && t('forms.required-field', {label: tr("shared.dealers")})) ||
                                                                 error?.message
                                                             }
                                                             {...field}
                                                         >
                                                             {dealers.map((option: any) => (
                                                                 <MenuItem key={option.value} value={option.value}>
                                                                     {option.label}
                                                                 </MenuItem>
                                                             ))}
                                                         </TextField>
                                                     )}
                                                 />
                                             </Grid>
                                         </Grid>
                                     </>
                                 }
                                 <FileUploader
                                     label={t('buttons.importFromFile')}
                                     onUpload={(file: File) => {
                                         setFileForImport(file);
                                         setOpenUploadFile(false);
                                         setDealers([]);

                                     }}
                                     acceptedFiles={{'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx']}}
                                     disabled={legalEntityOptions.length > 1 && (!watch("legalEntity") || !watch("dealer"))}
                                 />
                             </>
                         )}
            />
        </>
    );
}

export default PoiListPage;